import type { Entry } from '@taler/api-client'
import { absMoney, type CurrencyCode, type Money, sumMoney } from '@taler/domain'

export interface OwnAmount {
  /** One figure per currency; a single one unless no rate covers the entry's date. */
  readonly figures: readonly Money[]
  /** No rate covers the entry's date: `figures` are then native, one per currency posted. */
  readonly rateMissing: boolean
}

type Posting = Entry['postings'][number]

export function postingFigures(postings: readonly Posting[], displayCurrency: CurrencyCode): OwnAmount {
  const converted = postings.flatMap((posting) =>
    posting.displayAmount === undefined ? [] : [posting.displayAmount],
  )
  if (converted.length === postings.length) {
    return { figures: [sumMoney(converted, displayCurrency)], rateMissing: false }
  }
  const currencies = [...new Set(postings.map((posting) => posting.amount.currency))]
  return {
    figures: currencies.map((currency) =>
      sumMoney(
        postings.filter((posting) => posting.amount.currency === currency).map((posting) => posting.amount),
        currency,
      ),
    ),
    rateMissing: true,
  }
}

/** The share of an entry posted to `accountIds`, in the display currency as the backend converted it. */
export function ownAmount(
  entry: Entry,
  accountIds: ReadonlySet<string>,
  displayCurrency: CurrencyCode,
): OwnAmount {
  return postingFigures(
    entry.postings.filter((posting) => accountIds.has(posting.accountId)),
    displayCurrency,
  )
}

/** The magnitude leaving the given source legs of a movement. */
export function movedAmount(sourceLegs: readonly Posting[], displayCurrency: CurrencyCode): OwnAmount {
  const sum = postingFigures(sourceLegs, displayCurrency)
  return { figures: sum.figures.map(absMoney), rateMissing: sum.rateMissing }
}
